import { inventoryRuleActionSchema } from "@redotech/redo-inventory-common/inventory-rules/rule-definition";
import {
  inventoryItemIdFormat,
  inventoryRuleIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";
import {
  inventoryRuleChangeSchema,
  inventoryRuleCoverageSchema,
} from "./inventory-rule-models";

export const PREVIEW_ITEM_LIMIT = 50;

/**
 * Per scheduled action rule the change newly reaches, the items already past its day.
 * Empty when nothing would act on them, so the save needs no choice.
 */
export const previewInventoryRuleImpact = unaryRpc({
  name: "previewInventoryRuleImpact" as const,
  input: z.object({ change: inventoryRuleChangeSchema }),
  output: z.object({
    steps: z.array(
      z.object({
        /** The lane step, or 0 for a rule change. */
        stepIndex: z.int().min(0),
        /** The action within the lane step, or 0 for a rule change. */
        actionIndex: z.int().min(0),
        /** Unset for a step that isn't saved yet. */
        ruleId: inventoryRuleIdFormat.schema.nullable(),
        day: z.int().min(0),
        action: inventoryRuleActionSchema,
        itemCount: z.int().min(0),
        /** The first items, oldest on their clock first. */
        items: z
          .array(
            z.object({
              inventoryItemId: inventoryItemIdFormat.schema,
              sku: z.string().nullable(),
              grade: z.string().nullable(),
              daysOnClock: z.int().min(0),
            }),
          )
          .max(PREVIEW_ITEM_LIMIT),
      }),
    ),
    /**
     * Each item the steps would act on, counted once under the action of the last
     * step it's past, which is where the change leaves it.
     */
    outcomes: z.array(
      z.object({
        action: inventoryRuleActionSchema,
        itemCount: z.int().min(1),
      }),
    ),
  }),
});
export type InventoryRuleImpactPreview = z.infer<
  typeof previewInventoryRuleImpact.output
>;

/** The coverage analyzer over the warehouse's saved rules, or with an unsaved change applied. */
export const getInventoryRuleCoverage = unaryRpc({
  name: "getInventoryRuleCoverage" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    change: inventoryRuleChangeSchema.optional(),
  }),
  output: inventoryRuleCoverageSchema,
});
